import type { BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";
import { normalizeLicensePlateForStorage } from "@/lib/licensePlate";

export type CompanionFieldMode = "off" | "optional" | "required";

export type CompanionFieldKey =
  | "title"
  | "first_name"
  | "middle_name"
  | "last_name"
  | "company_name"
  | "vat_id"
  | "email"
  | "phone"
  | "license_plate"
  | "age"
  | "date_of_birth"
  | "nationality"
  | "street"
  | "house_number"
  | "address_line_2"
  | "postal_code"
  | "city"
  | "state_region"
  | "country_code";

export type CompanionFieldsConfig = Record<CompanionFieldKey, CompanionFieldMode>;

/** Adressbestandteile, die gemeinsam über die Adressabfrage gesteuert werden. */
export const GUEST_ADDRESS_FIELD_KEYS: CompanionFieldKey[] = [
  "street",
  "house_number",
  "address_line_2",
  "postal_code",
  "city",
  "state_region",
  "country_code",
];

const GUEST_ADDRESS_CORE_KEYS: CompanionFieldKey[] = ["street", "postal_code", "city"];

export function guestAddressInquiryEnabled(fields: CompanionFieldsConfig): boolean {
  return GUEST_ADDRESS_CORE_KEYS.some((key) => fields[key] !== "off");
}

/** Leitet den gemeinsamen Adressmodus aus Straße, PLZ und Ort ab. */
export function getGuestAddressMode(fields: CompanionFieldsConfig): CompanionFieldMode {
  const coreModes = GUEST_ADDRESS_CORE_KEYS.map((key) => fields[key]);
  if (coreModes.every((mode) => mode === "off")) return "off";
  if (coreModes.every((mode) => mode === "required")) return "required";
  return "optional";
}

export function applyGuestAddressMode(
  fields: CompanionFieldsConfig,
  mode: CompanionFieldMode,
): CompanionFieldsConfig {
  const next = { ...fields };
  if (mode === "off") {
    for (const key of GUEST_ADDRESS_FIELD_KEYS) {
      next[key] = "off";
    }
    return next;
  }
  for (const key of GUEST_ADDRESS_CORE_KEYS) {
    next[key] = mode;
  }
  next.house_number = "optional";
  next.address_line_2 = "off";
  next.state_region = "off";
  next.country_code = "optional";
  return next;
}

export function applyGuestAddressInquiry(
  fields: CompanionFieldsConfig,
  enabled: boolean,
): CompanionFieldsConfig {
  return applyGuestAddressMode(fields, enabled ? "required" : "off");
}

/** Ergänzt Hausnummer und Land, wenn die Adressabfrage aktiv ist. */
export function normalizeGuestAddressFieldModes(
  fields: CompanionFieldsConfig,
): CompanionFieldsConfig {
  if (!guestAddressInquiryEnabled(fields)) return fields;
  const next = { ...fields };
  if (next.house_number === "off") next.house_number = "optional";
  if (next.country_code === "off") next.country_code = "optional";
  return next;
}

export type CompanionFieldsConfigByRole = {
  adult: CompanionFieldsConfig;
  child: CompanionFieldsConfig;
};

export type CompanionRole = "adult" | "child";

export type CompanionFormData = Record<CompanionFieldKey, string> & {
  role: CompanionRole;
};

export const COMPANION_FIELD_KEYS: CompanionFieldKey[] = [
  "title",
  "first_name",
  "middle_name",
  "last_name",
  "company_name",
  "vat_id",
  "email",
  "phone",
  "license_plate",
  "age",
  "date_of_birth",
  "nationality",
  "street",
  "house_number",
  "address_line_2",
  "postal_code",
  "city",
  "state_region",
  "country_code",
];

export const COMPANION_FIELD_LABELS: Record<CompanionFieldKey, string> =
  getCompanionFieldLabels(de);

export function getCompanionFieldLabels(
  messages: BookingMessages,
): Record<CompanionFieldKey, string> {
  const labels = messages.companionFields;
  return {
    title: labels.title,
    first_name: labels.first_name,
    middle_name: labels.middle_name,
    last_name: labels.last_name,
    company_name: labels.company_name,
    vat_id: labels.vat_id,
    email: labels.email,
    phone: labels.phone,
    license_plate: labels.license_plate,
    age: labels.age,
    date_of_birth: labels.date_of_birth,
    nationality: labels.nationality,
    street: labels.street,
    house_number: labels.house_number,
    address_line_2: labels.address_line_2,
    postal_code: labels.postal_code,
    city: labels.city,
    state_region: labels.state_region,
    country_code: labels.country_code,
  };
}

export function emptyCompanion(role: CompanionRole): CompanionFormData {
  return {
    role,
    title: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    company_name: "",
    vat_id: "",
    email: "",
    phone: "",
    license_plate: "",
    age: "",
    date_of_birth: "",
    nationality: "",
    street: "",
    house_number: "",
    address_line_2: "",
    postal_code: "",
    city: "",
    state_region: "",
    country_code: "DE",
  };
}

export function expectedCompanionRoles(adults: number, children: number): CompanionRole[] {
  const roles: CompanionRole[] = [];
  for (let i = 0; i < Math.max(0, adults - 1); i += 1) roles.push("adult");
  for (let i = 0; i < Math.max(0, children); i += 1) roles.push("child");
  return roles;
}

export function companionSlotLabel(
  role: CompanionRole,
  index: number,
  messages?: Pick<BookingMessages, "companionFields">,
): string {
  if (messages) {
    return role === "child"
      ? `${messages.companionFields.child} ${index + 1}`
      : `${messages.companionFields.adult} ${index + 1}`;
  }
  if (role === "child") return `Kind ${index + 1}`;
  return `Erwachsene/r ${index + 1}`;
}

export function companionFieldConfigForRole(
  configs: CompanionFieldsConfigByRole,
  role: CompanionRole,
): CompanionFieldsConfig {
  return role === "child" ? configs.child : configs.adult;
}

export function resolveCompanionFieldConfigs(
  config: Partial<CompanionFieldsConfigByRole> & { companion_fields?: CompanionFieldsConfig },
): CompanionFieldsConfigByRole {
  const fallback = config.companion_fields ?? DEFAULT_COMPANION_FIELDS;
  return {
    adult: { ...DEFAULT_COMPANION_FIELDS, ...fallback, ...config.adult },
    child: { ...DEFAULT_COMPANION_FIELDS, ...fallback, ...config.child },
  };
}

export const DEFAULT_COMPANION_FIELDS: CompanionFieldsConfig = {
  title: "off",
  first_name: "required",
  last_name: "required",
  middle_name: "off",
  company_name: "off",
  vat_id: "off",
  email: "off",
  phone: "off",
  license_plate: "off",
  age: "off",
  date_of_birth: "off",
  nationality: "off",
  street: "off",
  house_number: "off",
  address_line_2: "off",
  postal_code: "off",
  city: "off",
  state_region: "off",
  country_code: "off",
};

export function buildCompanionsPayload(
  companions: CompanionFormData[],
  configs: CompanionFieldsConfigByRole,
): Record<string, string | null>[] {
  return companions.map((c) => {
    const config = companionFieldConfigForRole(configs, c.role);
    const row: Record<string, string | null> = { role: c.role };
    for (const key of COMPANION_FIELD_KEYS) {
      const mode = config[key];
      if (mode === "off" || key === "age") continue;
      const raw = c[key];
      if (key === "license_plate") {
        row[key] = raw.trim() ? normalizeLicensePlateForStorage(raw) : null;
      } else if (key === "country_code") {
        row[key] = raw.trim() ? raw.trim().toUpperCase().slice(0, 2) : "DE";
      } else {
        row[key] = raw.trim() ? raw.trim() : null;
      }
    }
    return row;
  });
}

export function validateCompanions(
  companions: CompanionFormData[],
  configs: CompanionFieldsConfigByRole,
  messages?: BookingMessages,
): string | null {
  const labels = messages ? getCompanionFieldLabels(messages) : COMPANION_FIELD_LABELS;
  let adultIndex = 0;
  let childIndex = 0;
  for (const c of companions) {
    const slotIndex = c.role === "child" ? childIndex++ : adultIndex++;
    const config = companionFieldConfigForRole(configs, c.role);
    for (const key of COMPANION_FIELD_KEYS) {
      if (config[key] !== "required") continue;
      if (!c[key].trim()) {
        const slot = companionSlotLabel(c.role, slotIndex, messages);
        return messages
          ? messages.validation.companionFieldRequired(slot, labels[key])
          : `${slot}: ${labels[key]} ist erforderlich.`;
      }
    }
  }
  return null;
}
